import { escapeHtml } from '../format'
import type { DiscoveredRow } from '../../api/inventoree'
import { precheckVia, precheckWords, readinessWords } from './readiness'

/**
 * DiscoveredDetails.render din public/js/discovery.js: patru grupe + porturi.
 * Firul de prezenta (alt cap Discovery) nu se cere aici.
 */

const LABELS: Record<string, string> = {
  device_name: 'Device name',
  internal_ip: 'Internal IP',
  mac: 'MAC',
  os: 'Operating system',
  device_type_name: 'Type',
  manufacturer_name: 'Manufacturer',
  hints: 'Detection hints',
  uuid: 'UUID',
  virtual_host_unique: 'Virtual host',
  virtual_host_mac: 'Virtual host MAC',
  relay_name: 'Discovered by',
  first_saw: 'First saw',
  last_saw: 'Last seen',
  method: 'Method',
  deploy_status: 'Deploy status',
  deploy_problem: 'Deploy problem',
  /* `can_deploy` si `is_win_server` scriau "No" pe fiecare aparat: niciun
     agent nu le trimite. In locul lor, ce se socoteste din porturi si proba. */
  deploy_ready: 'Agent can be deployed',
  precheck_result: 'Live check',
  precheck_via: 'Live check path',
  precheck_at: 'Live check at',
  precheck_problem: 'Live check reason',
  od_name: 'Already in inventory as',
  ID: 'Discovery ID',
}

const METHODS: Record<string, string> = { '1': 'Network scan', '2': 'Added manually' }
const DEPLOY: Record<string, string> = { '1': 'Pending', '2': 'Ok', '3': 'Error' }

const GROUPS: [string, string[]][] = [
  ['Device', ['device_name', 'internal_ip', 'mac', 'manufacturer_name', 'uuid']],
  ['What it is', ['device_type_name', 'os', 'hints', 'virtual_host_unique', 'virtual_host_mac']],
  ['Scan', ['relay_name', 'method', 'first_saw', 'last_saw', 'ID']],
  ['Agent', ['deploy_ready', 'precheck_result', 'precheck_via', 'precheck_at', 'precheck_problem', 'deploy_status', 'deploy_problem', 'od_name']],
]

function show(key: string, value: unknown): string {
  if (value === null || value === undefined) return ''
  const text = String(value).trim()
  if (text === '' || text === '0000-00-00 00:00:00') return ''
  if (key === 'ID' && text === '0') return ''
  if (key === 'deploy_ready') return escapeHtml(readinessWords(text))
  if (key === 'precheck_result') return escapeHtml(precheckWords(text))
  if (key === 'precheck_via') return escapeHtml(precheckVia(text))
  if (key === 'method') return METHODS[text] || text
  if (key === 'deploy_status') return DEPLOY[text] || ''
  if (key === 'open_ports') return escapeHtml(text.replace(/\s*,\s*/g, ', '))
  return escapeHtml(text)
}

function group(source: Record<string, unknown>, title: string, keys: string[]): string {
  let rows = ''
  for (const key of keys) {
    const value = show(key, source[key])
    if (value === '') continue
    rows += `<div class="dd-fact"><span class="dd-k">${escapeHtml(LABELS[key] || key)}</span><span class="dd-v">${value}</span></div>`
  }
  if (rows === '') return ''
  return `<section class="dd-group"><h6>${escapeHtml(title)}</h6>${rows}</section>`
}

export function discoveredDetailsHtml(row: DiscoveredRow): string {
  const source = row as unknown as Record<string, unknown>
  let body = ''
  for (const [title, keys] of GROUPS) body += group(source, title, keys)

  const ports = String(row.open_ports ?? '').trim()
  let portsHtml = ''
  if (ports !== '') {
    const list = ports.split(/\s*,\s*/).filter((one) => one !== '')
    portsHtml = `<section class="dd-group dd-group-wide"><h6>Open ports (${list.length})</h6><div class="dd-ports">`
    for (const port of list) portsHtml += `<span class="dd-port">${escapeHtml(port)}</span>`
    portsHtml += '</div></section>'
  }

  let head = `<div class="dd-head"><span class="dd-title">${escapeHtml(String(row.device_name || row.internal_ip || `#${row.ID}`))}</span>`
  if (String(row.device_type_name || '').trim() !== '') {
    head += `<span class="dd-chip">${escapeHtml(row.device_type_name ?? '')}</span>`
  }
  if (String(row.relay_name || '').trim() !== '') {
    head += `<span class="dd-chip dd-chip-quiet">seen by ${escapeHtml(row.relay_name ?? '')}</span>`
  }
  head += '</div>'

  if (body === '' && portsHtml === '') {
    body = '<section class="dd-group dd-group-wide">'
      + '<p class="dd-empty">The scan recorded nothing beyond the address. '
      + 'A device that answers only a ping tells us it is there and nothing more.</p>'
      + '</section>'
  }

  return `<div class="dd-panel">${head}<div class="dd-groups">${body}${portsHtml}</div></div>`
}
